import { useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Download,
  FileJson,
  Settings2,
  ShieldCheck,
  Upload,
  Wand2,
  ReceiptIndianRupee,
} from "lucide-react";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminSection } from "@/components/admin/AdminSection";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  exportSystemConfig,
  getSystemOverview,
  importSystemConfig,
  updateSystemSettings,
} from "@/lib/admin/system/system.functions";
import { PROTECTED_DATA } from "@/lib/admin/system/system.types";
import { getAdminSiteSettings } from "@/lib/admin/site-settings.functions";
import { GSTSettingsCard } from "@/components/admin/GSTSettingsCard";
import { FoodLiveActivation } from "@/components/admin/FoodLiveActivation";

export const Route = createFileRoute("/_admin/admin/system-configuration")({
  head: () => ({
    meta: [
      { title: "System Configuration — Admin" },
      { property: "og:title", content: "System Configuration — Poonam Kapur Healthy Kitchen Admin" },
      { property: "og:description", content: "Authorized administration of Poonam Kapur Healthy Kitchen settings." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      {
        name: "description",
        content:
          "Switch between Demonstration and Production mode and move configuration between environments.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SystemConfigurationPage,
});

function SystemConfigurationPage() {
  const qc = useQueryClient();
  const fetchOverview = useServerFn(getSystemOverview);
  const update = useServerFn(updateSystemSettings);
  const exportConfig = useServerFn(exportSystemConfig);
  const importConfig = useServerFn(importSystemConfig);
  const fileRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["admin", "system-overview"],
    queryFn: () => fetchOverview(),
  });

  const fetchSiteSettings = useServerFn(getAdminSiteSettings);
  const siteSettings = useQuery({
    queryKey: ["admin", "site-settings"],
    queryFn: () => fetchSiteSettings(),
  });

  const mode = data?.settings.system_mode ?? "demo";
  const superAdmin = data?.settings.is_super_admin === true;

  const modeMutation = useMutation({
    mutationFn: (next: "demo" | "production") => update({ data: { system_mode: next } }),
    onSuccess: (_r, next) => {
      toast.success(next === "demo" ? "Demonstration Mode enabled." : "Production Mode enabled.");
      void qc.invalidateQueries({ queryKey: ["admin", "system-overview"] });
    },
    onError: () => toast.error("Mode could not be changed."),
  });

  const handleExport = async () => {
    setBusy(true);
    try {
      const payload = await exportConfig();
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `configuration-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Configuration exported.");
    } catch {
      toast.error("Export failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleImport = async (file: File) => {
    setBusy(true);
    try {
      const payload = JSON.parse(await file.text());
      await importConfig({ data: { payload } });
      toast.success("Configuration imported.");
      void qc.invalidateQueries({ queryKey: ["admin"] });
    } catch {
      toast.error("That file is not a valid configuration export.");
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="System Configuration"
        description="Control how the platform behaves for demonstrations versus a live client, and move settings between environments."
      />

      <AdminSection
        number={1}
        icon={Settings2}
        title="Operating Mode"
        description="Demonstration Mode keeps sample records visible and shows a banner. Production Mode hides every demo record and shows professional empty states."
        actions={
          <Badge variant={mode === "production" ? "default" : "secondary"}>
            {mode === "production" ? "Production Mode" : "Demonstration Mode"}
          </Badge>
        }
      >
        <div className="flex items-center gap-3">
          <Switch
            id="mode"
            checked={mode === "production"}
            disabled={!superAdmin || modeMutation.isPending}
            onCheckedChange={(v) => modeMutation.mutate(v ? "production" : "demo")}
          />
          <Label htmlFor="mode" className="text-sm">
            Production Mode
          </Label>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          {superAdmin
            ? "Switching mode never deletes anything — use Production Cleanup to remove demo records permanently."
            : "Only a Super Admin can change the operating mode."}
        </p>
      </AdminSection>

      <AdminSection
        number={2}
        icon={FileJson}
        title="Configuration Export & Import"
        description="Carry a fully configured setup from one environment to another without touching transactional data."
      >
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => void handleExport()} disabled={busy} className="gap-2">
            <Download className="h-4 w-4" aria-hidden /> Export Configuration
          </Button>
          <Button
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={busy || !superAdmin}
            className="gap-2"
          >
            <Upload className="h-4 w-4" aria-hidden /> Import Configuration
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="application/json"
            className="sr-only"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleImport(f);
            }}
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          Exports include system settings, website content settings, categories, delivery zones and templates — never customers, orders or payments.
        </p>
      </AdminSection>

      <AdminSection
        number={3}
        icon={ShieldCheck}
        title="Protected Configuration"
        description="These records are always preserved by Production Cleanup and by every restore."
        defaultOpen={false}
      >
        <div className="flex flex-wrap gap-2">
          {PROTECTED_DATA.map((p) => (
            <Badge key={p} variant="secondary" className="font-normal">
              {p}
            </Badge>
          ))}
        </div>
      </AdminSection>

      <AdminSection
        number={4}
        icon={ReceiptIndianRupee}
        title="GST / Tax Configuration"
        description="Manage sitewide GST registration and tax application rules for food orders."
      >
        {siteSettings.data ? (
          <GSTSettingsCard
            data={siteSettings.data}
            onSaved={async () => {
              await qc.invalidateQueries({ queryKey: ["admin", "site-settings"] });
            }}
          />
        ) : (
          <p className="text-sm text-muted-foreground">Loading settings...</p>
        )}
      </AdminSection>

      <AdminSection
        number={5}
        icon={Wand2}
        title="First-Time Setup"
        description="New client? The Implementation Wizard walks through every configuration step in order."
        defaultOpen={false}
      >
        <Button asChild variant="outline" size="sm">
          <a href="/admin/implementation-wizard">Open Implementation Wizard</a>
        </Button>
      </AdminSection>
      <FoodLiveActivation />
    </div>
  );
}
